<script setup lang="ts">
import { onMounted, watch } from 'vue';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';
import { getB05Options, loadCmBaseCodeOptions } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import type { NoticeCorporateFormModel } from './composables/useFormPayload';

const props = defineProps<{
  model: NoticeCorporateFormModel;
  readOnly?: boolean;
}>();

const emit = defineEmits<{
  (e: 'request-address-lookup', context: Record<string, unknown>): void;
}>();

function requestRegisteredOfficeInput() {
  if (props.readOnly) return;
  emit('request-address-lookup', {
    fieldGroup: 'controllerRegisteredOffice',
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: 'Registered office address',
  });
}

function requestAlternateAddressInput() {
  if (props.readOnly) return;
  emit('request-address-lookup', {
    fieldGroup: 'controllerAlternateAddress',
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: 'Alternate address',
  });
}

onMounted(async () => {
  try {
    await loadCmBaseCodeOptions('B05');
  } catch (e) {
    console.warn('[NoticeCorporateContactProfileFields] B05 load failed', e);
  }
});

watch(
  () => props.model.controllerCountryCd,
  (cd) => {
    if (!cd) return;
    const opt = getB05Options().find((o) => o.value === cd);
    if (opt?.title) props.model.controllerCountryLabel = opt.title;
  },
  { immediate: true },
);
</script>

<template>
  <div class="nc-profile-fields">
    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model="model.controllerRegisteredOffice"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
          data-required-key="controllerRegisteredOffice"
          :class="{ 'ad-required-field': true }"
        >
          <template #label>Registered office address <span class="ad-required-mark">*</span></template>
          <template #append-inner>
            <v-tooltip location="top" :open-delay="150">
              <template #activator="{ props: tProps }">
                <v-btn
                  v-bind="tProps"
                  icon="mdi-creation"
                  size="x-small"
                  variant="text"
                  color="primary"
                  :disabled="!!readOnly"
                  @click.stop="requestRegisteredOfficeInput"
                />
              </template>
              <span>Enter address in chat</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model="model.controllerAlternateAddress"
          label="Alternate address"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
        >
          <template #append-inner>
            <v-tooltip location="top" :open-delay="150">
              <template #activator="{ props: tProps }">
                <v-btn
                  v-bind="tProps"
                  icon="mdi-creation"
                  size="x-small"
                  variant="text"
                  color="primary"
                  :disabled="!!readOnly"
                  @click.stop="requestAlternateAddressInput"
                />
              </template>
              <span>Enter address in chat</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" md="4">
        <CmBaseCodeAutocomplete
          v-model="model.controllerCountryCd"
          main-code="B05"
          label="Mobile country *"
          :read-only="!!readOnly"
          data-required-key="controllerCountryCd"
          required-mark
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="model.controllerAreaCode"
          label="Area code"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="model.controllerMobileNo"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :readonly="!!readOnly"
          data-required-key="controllerMobileNo"
          :class="{ 'ad-required-field': true }"
        >
          <template #label>Contact no <span class="ad-required-mark">*</span></template>
        </v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.nc-profile-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
  margin-bottom: 20px;
}

.nc-profile-fields :deep(.v-row) {
  margin-top: 0;
  margin-bottom: 0;
}

.nc-profile-fields :deep(.v-row + .v-row) {
  margin-top: 4px;
}

.ad-required-mark {
  color: #e53935;
}
</style>
